import { useState, useEffect, useCallback } from 'react';
import { getCoins, getCoinDetail, getMarketChart, searchCoins } from '../lib/api';
import { Coin, CoinDetail, MarketChart } from '../types';

export const useCoins = (vs_currency = 'usd') => {
  const [coins, setCoins] = useState<Coin[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCoins = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getCoins(vs_currency);
      setCoins(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch coins';
      setError(errorMessage);
      console.error('Coin fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [vs_currency]);

  useEffect(() => {
    fetchCoins();
    // Refresh every 2 minutes to avoid rate limits
    const interval = setInterval(fetchCoins, 120000);
    return () => clearInterval(interval);
  }, [fetchCoins]);

  return { coins, loading, error, refetch: fetchCoins };
};

export const useCoinDetail = (id: string, vs_currency = 'usd') => {
  const [coin, setCoin] = useState<CoinDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchCoin = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getCoinDetail(id, vs_currency);
        setCoin(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch coin');
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchCoin();
  }, [id, vs_currency]);

  return { coin, loading, error };
};

export const useMarketChart = (id: string, days: number = 7, vs_currency = 'usd') => {
  const [chart, setChart] = useState<MarketChart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchChart = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getMarketChart(id, days, vs_currency);
        setChart(data);
        setError(null);
      } catch (err: any) {
        console.log('Chart fetch error:', err.message);
        setError(err instanceof Error ? err.message : 'Failed to fetch chart');
        // Don't clear existing chart data on error
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchChart();
  }, [id, days, vs_currency]);

  return { chart, loading, error };
};

export const useSearchCoins = () => {
  const [results, setResults] = useState<Coin[]>([]);
  const [loading, setLoading] = useState(false);

  const search = useCallback(async (query: string) => {
    if (!query || query.length < 2) {
      setResults([]);
      return;
    }

    setLoading(true);
    try {
      const data = await searchCoins(query);
      setResults(data);
    } catch (error) {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  return { results, loading, search };
};